import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { EntityType } from '../models/entitlements/entity';
import { isEmpty } from '../utils/object-util';
import { CareCoordinatorService } from './care-coordinator.service';
import { CareGiverService } from './care-giver.service';
import { ExternalAgencyUserService } from './external-agency-user.service';
import { HospitalUserService } from './hospital-user.service';
import { PatientService } from './patient.service';
import { ProviderService } from './provider.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class EntityNameCacheService {
  private cache: Map<string, Observable<string>> = new Map();

  constructor(
    private providerService: ProviderService,
    private patientService: PatientService,
    private careGiverService: CareGiverService,
    private careCoordinatorService: CareCoordinatorService,
    private externalAgencyUserService: ExternalAgencyUserService,
    private hospitalUserService: HospitalUserService,
    private userService: UserService
  ) {}

  /**
   * Cache's an entity's name. If the name has already been cached, this method does nothing.
   * If the name has not already been cached, the name is cached asynchronously.
   *
   * @param entityId entityId whose name we are caching
   * @param entityType entityType whose name we are caching
   * @param forceRefresh `true` to force the cache to be refreshed
   */
  private cacheName(entityId: string | number, entityType: EntityType, forceRefresh = false) {
    if (isEmpty(entityId)) {
      return;
    }

    const cacheKey = this.getCacheKey(entityId, entityType);

    if (!forceRefresh && this.cache.has(cacheKey)) {
      return;
    }

    switch (entityType) {
      case EntityType.PROVIDER:
        this.cacheProviderName(String(entityId));
        break;

      case EntityType.PATIENT:
        this.cachePatientName(String(entityId));
        break;

      case EntityType.CAREGIVER:
        this.cacheCareGiverName(String(entityId));
        break;

      case EntityType.CARECOORDINATOR:
        this.cacheCareManagerName(String(entityId));
        break;

      case EntityType.EXTERNALAGENCY:
        this.cacheExternalAgencyUserName(String(entityId));
        break;

      case EntityType.HOSPITALUSER:
        this.cacheHospitalUserUserName(String(entityId));
        break;
      case EntityType.USER:
        this.cacheUserName(Number(entityId));
        break;
    }
  }

  /**
   * Fetches a name from the cache.
   *
   * @param entityId entityId whose name we want to fetch
   * @param entityType entityType whose name we want to fetch
   */
  getCachedName(entityId: string | number, entityType: EntityType, forceRefresh = false): Observable<string> {
    const cacheKey = this.getCacheKey(entityId, entityType);

    this.cacheName(entityId, entityType, forceRefresh);

    return this.cache.get(cacheKey);
  }

  /**
   * Removes everything from the cache.
   */
  clearCache() {
    this.cache.clear();
  }

  private getCacheKey(entityId: string | number, entityType: EntityType): string {
    return `${entityType}:${entityId}`;
  }

  private cacheProviderName(providerId: string) {
    this.updateCache(providerId, EntityType.PROVIDER, this.providerService.getProviderName(providerId));
  }

  private cachePatientName(memberId: string) {
    this.updateCache(memberId, EntityType.PATIENT, this.patientService.getPatientName(memberId));
  }

  private cacheCareGiverName(careGiverId: string) {
    this.updateCache(careGiverId, EntityType.CAREGIVER, this.careGiverService.getCareGiverName(careGiverId));
  }

  private cacheCareManagerName(careManagerId: string) {
    this.updateCache(
      careManagerId,
      EntityType.CARECOORDINATOR,
      this.careCoordinatorService.getCareManagerName(careManagerId)
    );
  }

  private cacheExternalAgencyUserName(externalAgencyUserId: string) {
    this.updateCache(
      externalAgencyUserId,
      EntityType.EXTERNALAGENCY,
      this.externalAgencyUserService.getExternalAgencyUserName(externalAgencyUserId)
    );
  }

  private cacheHospitalUserUserName(hospitalUserID: string) {
    this.updateCache(
      hospitalUserID,
      EntityType.HOSPITALUSER,
      this.hospitalUserService.getHospitalUserUserName(hospitalUserID)
    );
  }

  private cacheUserName(userId: number) {
    this.updateCache(userId, EntityType.USER, this.userService.getUserName(userId));
  }

  private updateCache(entityId: string | number, entityType: EntityType, entityNameObservable: Observable<string>) {
    const cacheKey = this.getCacheKey(entityId, entityType);
    const cacheValue = entityNameObservable.pipe(shareReplay(1));

    this.cache.set(cacheKey, cacheValue);
  }
}
